import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUpload = ({ 
  onFileSelect = () => {},
  acceptedFormats = '.xlsx,.xls',
  maxSizeMB = 10
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const maxSize = maxSizeMB * 1024 * 1024;
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

    if (!validTypes?.includes(file?.type)) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return false;
    }

    if (file?.size > maxSize) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return false;
    }

    return true;
  };

  const handleFile = (file) => {
    setError('');
    if (validateFile(file)) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFile(e?.target?.files?.[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setError('');
    onFileSelect(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats}
        onChange={handleChange}
        className="hidden"
        aria-label="File upload input"
      />
      {!selectedFile ? (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-6 md:p-8 transition-smooth ${
            dragActive
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
          }`}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={32} color="var(--color-primary)" />
            </div>
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
              Upload Financial Data
            </h3>
            <p className="text-sm text-muted-foreground font-body mb-4">
              Drag and drop your Excel file here, or click to browse
            </p>
            <Button
              variant="outline"
              size="default"
              onClick={handleButtonClick}
              iconName="FileSpreadsheet"
              iconPosition="left"
            >
              Choose File
            </Button>
            <p className="text-xs text-muted-foreground font-caption mt-4">
              Supported formats: .xlsx, .xls (Max {maxSizeMB}MB)
            </p>
          </div>
        </div>
      ) : (
        <div className="border-2 border-success/20 bg-success/5 rounded-lg p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="FileSpreadsheet" size={24} color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground font-body truncate">
                {selectedFile?.name}
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1">
                {formatFileSize(selectedFile?.size)}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
                <span className="text-xs text-success font-caption">File uploaded successfully</span>
              </div>
            </div>
            <button
              onClick={handleRemove}
              className="p-2 rounded-md hover:bg-destructive/10 transition-smooth"
              aria-label="Remove file"
            >
              <Icon name="X" size={18} color="var(--color-destructive)" />
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive font-body">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;